import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { Reveal } from '@/components/generals/reveal'
import { Wordmark } from '@/components/generals/wordmark'
import { NewsletterForm } from '@/components/generals/newsletter-form'
import { footerSitemap } from '@/constants/site'

const YEAR = 2026

/**
 * Translucent with a backdrop blur, so the CTA bloom above still reads through
 * the top of the footer instead of being cut off by it.
 */
export const Footer: FC = (): ReactNode => {
    return (
        <footer
            id='footer'
            className='relative w-full border-t border-white/10 bg-black/56 backdrop-blur-[5px]'
        >
            <div className='relative z-1 mx-auto flex w-full max-w-[1200px] flex-col gap-14 px-6 pt-20 pb-8 md:px-8 lg:px-16 lg:pt-24'>
                <Reveal
                    y={18}
                    blur={8}
                >
                    <Link
                        href='/'
                        aria-label='Funnel, back to home'
                        className='inline-flex transition-opacity duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:opacity-70'
                    >
                        <Wordmark />
                    </Link>
                </Reveal>

                <div className='flex flex-col gap-12 lg:flex-row lg:justify-between lg:gap-16'>
                    <Reveal
                        y={18}
                        blur={8}
                        delay={0.05}
                        className='flex max-w-[460px] flex-col gap-4'
                    >
                        <div className='flex flex-col gap-2'>
                            <h2 className='text-lg leading-none font-medium tracking-[-0.02em] text-white'>
                                Join our newsletter
                            </h2>

                            <p className='text-[15px] leading-[1.55] tracking-[-0.01em] text-white/56'>
                                Sign up to our mailing list below and be the first to know about new updates. Don&rsquo;t worry, we hate spam too.
                            </p>
                        </div>

                        <NewsletterForm />
                    </Reveal>

                    <Reveal
                        y={18}
                        blur={8}
                        delay={0.1}
                        stagger={0.08}
                        className='grid grid-cols-2 gap-10 sm:gap-16 lg:shrink-0'
                    >
                        {footerSitemap.map((column) => (
                            <nav
                                key={column.heading}
                                aria-label={column.heading}
                                className='flex flex-col gap-3'
                            >
                                <h2 className='text-[15px] leading-none font-medium tracking-[-0.01em] text-white'>
                                    {column.heading}
                                </h2>

                                <ul className='flex flex-col gap-2.5'>
                                    {column.links.map((link) => (
                                        <li key={link.href}>
                                            <Link
                                                href={link.href}
                                                className='text-[15px] leading-none tracking-[-0.01em] text-white/56 transition-colors duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] hover:text-white'
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        ))}
                    </Reveal>
                </div>

                <div className='flex flex-col gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between'>
                    <p className='text-[13px] tracking-[-0.01em] text-white/40'>
                        &copy; {YEAR} Funnel. All rights reserved.
                    </p>

                    <p className='text-[13px] tracking-[-0.01em] text-white/40'>
                        Paid advertising, built for profitable growth.
                    </p>
                </div>
            </div>
        </footer>
    )
}
